import { QueryClient, useInfiniteQuery, keepPreviousData as useKeepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { OrderEndpoint, RoleMimetype } from 'constants/API'
import { OrderAssignmentStatusFilter, OwnershipScope, SortBy, SortDirection } from 'constants/misc'
import { ISODateString, PageableResponse } from 'models/misc'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { AxiosResponse } from 'axios'
import { RescheduleReason } from 'components/common/RescheduleOrder/RescheduleOrder.context'
import { DeclineAssignmentReason } from 'constants/assignment'
import { AssignmentDTOCreative } from 'models/assignment'
import { DealDTO, DealDTOAdministrator } from 'models/deal'
import { Nullable } from 'models/helpers'
import { StatusResponse } from 'models/redux'
import { useMemo } from 'react'
import { useAPI } from 'utils/API'
import { _invalidateAssignment } from './assignment.query'

enum Endpoints {
  GET = '/deal/{orderId}',
  EDIT_REFERENCE = '/deal/{orderId}/reference',
  CANCEL = '/deal/{orderId}/cancel',
  LIST = '/deal/all',
  LIST_WORKSPACE = '/workspace/{workspaceId}/orders',
  SYNC = '/deal/{orderId}/sync',
  UPDATE_START_DATE_TIME = '/deal/{orderId}/startDateTime',
  CLIENT_GALLERY_POWER_OF_ATTORNEY_SUBMIT = '/order/{orderId}/documents/power-of-attorney/submit',
  ACCEPT_ORDER = '/order/{orderId}/accept',
  DECLINE_ORDER = '/order/{orderId}/decline',
  RESCHEDULE_ORDER = '/order/{orderId}/reschedule',
  AUTOMATE_ORDER = '/order/{orderId}/automate',
  STOP_AUTOMATED_ORDER = '/order/{orderId}/stopAutomation',
  REVOKE_EXPRESS_BROADCAST = '/order/{orderId}/broadcast/revoke',
}

// QUERIES

/** Key of one order's read, as useGetOrder caches it */
export const orderQueryKey = (orderId: number) =>
  [EntityKeys.ORDER, QueryType.GET_ONE, { orderId }] as const

export function useGetOrder(orderId: number, enabled?: boolean, keepPreviousData = false) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: orderQueryKey(orderId),
    queryFn: () => api.get<DealDTO>(
      Endpoints.GET,
      { orderId: orderId.toString() },
      true
    ),
    enabled: enabled ?? !!orderId,
    placeholderData: keepPreviousData ? useKeepPreviousData : undefined
  })
}

/** Represents a file attached to assignment. */
export interface AssignmentAttachedFile {
  /** Name of the uploaded file */
  filename: string
  /** Signed URL for the request */
  signedURL: string
  /** A dictionary of headers for appending to the upload request */
  headers: Record<string, string>
  /** Creation date of file. */
  fileCreatedAt: string
}

export interface AssignmentAttachedFileResponse {
  /** Id of the file */
  id: string
  /** Signed URL for the request */
  fileUrl: string
  /** File name */
  fileName: string
  /** Received date of file. */
  receivedDate: string
}

export function useGetDocumentsOrderInvoices(orderId: number, enabled = false) {
  const api = useAPI<OrderEndpoint>()

  return useQuery<AssignmentAttachedFileResponse[]>({
    queryKey: [EntityKeys.ORDER_DOCUMENTS_INVOICES, QueryType.GET_ONE, { orderId }],
    queryFn: async () => {
      const response = await api.get<AssignmentAttachedFile[]>(
        OrderEndpoint.ORDER_DOCUMENTS_INVOICES,
        { orderId: orderId.toString() },
        true
      )
      return response.data.map(data => ({
        id: data.signedURL,
        fileName: data.filename,
        fileUrl: data.signedURL,
        receivedDate: data.fileCreatedAt,
      }))
    },
    enabled,
  })
}

// LIST QUERIES

export interface OrderListFilters {
  status: OrderAssignmentStatusFilter
  scope?: OwnershipScope
  userIds: string[]
  sortDirection?: SortDirection
  creationDateStart?: Nullable<moment.Moment>,
  creationDateEnd?: Nullable<moment.Moment>,
  shootingDateStart?: Nullable<moment.Moment>,
  shootingDateEnd?: Nullable<moment.Moment>,
  sortBy?: SortBy
}

interface OrderListPayload extends OrderListFilters {
  page: number
  size: number
  key: string
  search: Nullable<string>,
}

const defaultParams: OrderListPayload = {
  page: 0,
  size: 10,
  key: 'KEY',
  sortDirection: SortDirection.DESCENDING,
  status: OrderAssignmentStatusFilter.ALL,
  userIds: [],
  search: null,
  creationDateStart: null,
  creationDateEnd: null,
  shootingDateStart: null,
  shootingDateEnd: null,
  sortBy: SortBy.DEFAULT,
}

const validStatuses = new Set<string>(Object.values(OrderAssignmentStatusFilter))

const _serializeParams = (params: OrderListFilters & Partial<OrderListPayload>) => {

  const finalParams = {
    ...defaultParams,
    ...params,
  }

  const status = validStatuses.has(finalParams.status) ? finalParams.status : OrderAssignmentStatusFilter.ALL

  return {
    page: (finalParams.page || 0).toString(),
    size: finalParams.size.toString(),
    sortDirection: finalParams.sortDirection?.toString() ?? SortDirection.DESCENDING,
    filter: status,
    displayScope: finalParams.scope ? finalParams.scope.toString() : undefined,
    userIds: finalParams.scope !== OwnershipScope.INDIVIDUAL && finalParams.userIds.length > 0 ? finalParams.userIds.toString() : undefined,
    query: finalParams.search || undefined,
    creationDateStart: finalParams.creationDateStart?.toISOString(),
    creationDateEnd: finalParams.creationDateEnd?.toISOString(),
    shootingDateStart: finalParams.shootingDateStart?.toISOString(),
    shootingDateEnd: finalParams.shootingDateEnd?.toISOString(),
    sortBy: finalParams.sortBy?.toString() ?? SortBy.DEFAULT,
  }
}

export const useListOrders = <T = DealDTO>(listKey: string, params: OrderListFilters & Partial<OrderListPayload>, workspaceId?: string, enabled?: boolean) => {
  const api = useAPI<Endpoints>()

  const serializedParams = useMemo(() => _serializeParams(params), [params])
  const useWorkspaceEndpoint = Boolean(workspaceId)

  return useQuery({
    queryKey: useWorkspaceEndpoint
      ? [EntityKeys.ORDER, QueryType.LIST, 'WORKSPACE', workspaceId, listKey, { ...serializedParams }]
      : [EntityKeys.ORDER, QueryType.LIST, listKey, { ...serializedParams }],
    queryFn: () => useWorkspaceEndpoint
      ? api.get<PageableResponse<T[]>>(Endpoints.LIST_WORKSPACE, { workspaceId: workspaceId! }, true, { params: serializedParams })
      : api.get<PageableResponse<T[]>>(Endpoints.LIST, {}, true, { params: serializedParams }),
    enabled,
  })
}

export const useInfiniteListOrders = <T = DealDTO>(listKey: string, params: OrderListFilters, workspaceId?: string, enabled?: boolean) => {
  const api = useAPI<Endpoints>()

  const serializedParams = useMemo(() => _serializeParams(params), [params])
  const useWorkspaceEndpoint = Boolean(workspaceId)

  const fetchFnc = (page: number) => useWorkspaceEndpoint
    ? api.get<PageableResponse<T[]>>(
      Endpoints.LIST_WORKSPACE,
      { workspaceId: workspaceId! },
      true,
      { params: { ...serializedParams, page } }
    )
    : api.get<PageableResponse<T[]>>(
      Endpoints.LIST,
      {},
      true,
      { params: { ...serializedParams, page } }
    )

  return useInfiniteQuery({
    queryKey: useWorkspaceEndpoint
      ? [EntityKeys.ORDER, QueryType.INFINITE_LIST, 'WORKSPACE', workspaceId, listKey, { ...serializedParams }]
      : [EntityKeys.ORDER, QueryType.INFINITE_LIST, listKey, { ...serializedParams }],
    queryFn: ({ pageParam }) => fetchFnc(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => !lastPage.data.last ? lastPage.data.pageable.pageNumber + 1 : undefined,
    enabled,
  })
}


// MUTATIONS
export const _invalidateOrder = (client: QueryClient, orderId?: number) => {
  client.invalidateQueries({ queryKey: [EntityKeys.ORDER, QueryType.LIST] })
  client.invalidateQueries({ queryKey: [EntityKeys.ORDER, QueryType.INFINITE_LIST] })
  client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY_ORDERS, QueryType.LIST] })
  client.invalidateQueries({ queryKey: [EntityKeys.PROPERTY_REORDER_CONTEXT] })
  if (orderId) client.invalidateQueries({ queryKey: orderQueryKey(orderId) })
}

export function useSyncOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<DealDTO>, { orderId: number }>(
    ({ orderId }) => api.post(
      Endpoints.SYNC,
      { orderId: orderId.toString() },
      {},
      true,
      {
        headers: {
          Accept: RoleMimetype.ADMIN,
        }
      }
    ),
    (client, { orderId }) => _invalidateOrder(client, orderId)
  )
}

export function useAdminSyncOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<DealDTO>, { orderId: number }>(
    ({ orderId }) => api.post(
      Endpoints.SYNC,
      { orderId: orderId.toString() },
      {},
      true
    ),
    (client, { orderId }) => _invalidateOrder(client, orderId)
  )
}

interface EditOrderReferencePayload {
  orderId: number
  reference: string
}

export function useEditOrderReference() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, EditOrderReferencePayload>(
    ({ orderId, reference }) => api.put(
      Endpoints.EDIT_REFERENCE,
      { orderId: orderId.toString() },
      { reference },
      true
    ),
    (client, { orderId }) => _invalidateOrder(client, orderId)
  )
}

interface CancelOrderPayload {
  orderId: number
  cancellationReason: string
}

export function useCancelOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, CancelOrderPayload>(
    ({ orderId, cancellationReason }) => api.put(
      Endpoints.CANCEL,
      { orderId: orderId.toString() },
      { cancellationReason },
      true
    ),
    (client, { orderId }) => _invalidateOrder(client, orderId)
  )
}

interface UpdateStartDateTime {
  orderId: number
  startDateTime: ISODateString
}

export const useUpdateStartDateTime = () => {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, UpdateStartDateTime>(
    ({ orderId, startDateTime }) => api.put(
      Endpoints.UPDATE_START_DATE_TIME,
      { orderId: orderId.toString() },
      { value: startDateTime },
      true
    ),
    (client, { orderId }) => _invalidateOrder(client, orderId)
  )
}

export enum UploadOrderDocumentType {
  POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY',
}

interface SubmitPowerOfAttorneyPayload {
  orderId: number
  powerOfAttorneyFilename: string
  powerOfAttorneyRepresentativesFilename: Nullable<string>
}

/** Mutation function that submits power of attorney files uploaded to gallery */
export function useSubmitGalleryPowerOfAttorney() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, SubmitPowerOfAttorneyPayload>(
    ({ orderId, powerOfAttorneyFilename, powerOfAttorneyRepresentativesFilename }) => api.post(
      Endpoints.CLIENT_GALLERY_POWER_OF_ATTORNEY_SUBMIT,
      {
        orderId: orderId.toString(),
      },
      {
        powerOfAttorneyFilename,
        powerOfAttorneyRepresentativesFilename,
      },
      true
    ),
    (client, { orderId }) => {
      _invalidateOrder(client, orderId)
    }
  )
}

export function useAcceptOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<AssignmentDTOCreative[]>, { orderId: number }>(
    ({ orderId }) => api.post(
      Endpoints.ACCEPT_ORDER,
      { orderId: orderId.toString() },
      {},
      true
    ),
    (client) => {
      _invalidateOrder(client)
      _invalidateAssignment(client)
    }
  )
}

interface DeclinePayload {
  orderId: number
  reason: DeclineAssignmentReason
  reasonFreetext: Nullable<string>
  unavailabilityInterval: Nullable<{
    from: ISODateString
    to: ISODateString
  }>
}

export function useDeclineOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<AssignmentDTOCreative[]>, DeclinePayload>(
    ({ orderId, ...payload }) => api.post(
      Endpoints.DECLINE_ORDER,
      { orderId: orderId.toString() },
      payload,
      true
    ),
    (client) => {
      _invalidateOrder(client)
      _invalidateAssignment(client)
    }
  )
}

interface RescheduleOrderPayload {
  orderId: number
  newDateTime: ISODateString,
  hasCtAgreed: boolean,
  rescheduleReason: RescheduleReason,
  rescheduleComment: string
}

export function useRescheduleOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<DealDTO>, RescheduleOrderPayload>(
    ({ orderId, ...payload }) => api.put(
      Endpoints.RESCHEDULE_ORDER,
      { orderId: orderId.toString() },
      payload,
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT
        }
      }
    ),
    (client, { orderId }) => {
      _invalidateOrder(client, orderId)
      _invalidateAssignment(client)
    }
  )
}

export function useAutomateOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse, { orderId: number, assignmentId: string | null }>(
    ({ orderId }) => api.post(
      Endpoints.AUTOMATE_ORDER,
      { orderId: orderId.toString() },
      {},
      true,
      { headers: { Accept: RoleMimetype.ADMIN } }
    ),
    (client, { orderId, assignmentId }) => {
      _invalidateOrder(client, orderId)
      if (assignmentId) _invalidateAssignment(client, assignmentId)
    }
  )
}

export function useStopAutomatedOrder() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse, { orderId: number, assignmentId: string | null }>(
    ({ orderId }) => api.post(
      Endpoints.STOP_AUTOMATED_ORDER,
      { orderId: orderId.toString() },
      {},
      true,
      { headers: { Accept: RoleMimetype.ADMIN } }
    ),
    (client, { orderId, assignmentId }) => {
      _invalidateOrder(client, orderId)
      if (assignmentId) _invalidateAssignment(client, assignmentId)
    }
  )
}

export function useRevokeExpressBroadcast() {
  const api = useAPI<Endpoints>()
  const queryClient = useQueryClient()

  return getMutation<AxiosResponse<DealDTOAdministrator>, { orderId: number }>(
    ({ orderId }) => api.post(
      Endpoints.REVOKE_EXPRESS_BROADCAST,
      { orderId: orderId.toString() },
      {},
      true,
      { headers: { Accept: RoleMimetype.ADMIN } },
      // Expected when the broadcast closed meanwhile; the card shows the backend's message
      { ignoreErrorFactory: error => error.response?.status === 409 }
    ),
    (client, { orderId }) => {
      _invalidateOrder(client, orderId)
      // Every creative assignment of the order goes to ops, not only the one on screen
      client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET_ONE] })
      _invalidateAssignment(client)
    },
    {
      // A 409 means the broadcast closed meanwhile; the refetch shows how
      onError: (_error, { orderId }) => _invalidateOrder(queryClient, orderId),
    }
  )
}
